import React from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';

interface QRCodeErrorHandlerProps {
  error: {
    type: 'VENDOR_MISMATCH' | 'INVALID_QR' | 'NETWORK_ERROR' | 'NO_MENU' | 'CART_CONFLICT';
    message: string;
    details?: {
      expectedVendor?: number;
      actualVendor?: number;
      qrCode?: string;
      currentVendorName?: string;
      newVendorName?: string;
    };
  } | null;
  onClose: () => void;
  onClearCart?: () => void;
  onContinueWithCart?: () => void;
}

export const QRCodeErrorHandler: React.FC<QRCodeErrorHandlerProps> = ({ 
  error, 
  onClose, 
  onClearCart,
  onContinueWithCart 
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { clearCart } = useCart();

  if (!error) return null;

  const getErrorTitle = () => {
    switch (error.type) {
      case 'VENDOR_MISMATCH':
        return '⚠️ QR Code Mismatch';
      case 'INVALID_QR':
        return '❌ Invalid QR Code';
      case 'NETWORK_ERROR':
        return '📡 Connection Error';
      case 'NO_MENU':
        return '📋 Menu Not Available';
      case 'CART_CONFLICT':
        return '🛒 Different Restaurant Detected';
      default:
        return 'Error';
    }
  };

  const getErrorMessage = () => {
    switch (error.type) {
      case 'VENDOR_MISMATCH':
        return `This QR code belongs to a different restaurant than the one specified in the URL. 
                The QR code is for vendor ${error.details?.actualVendor}, but you're trying to access vendor ${error.details?.expectedVendor}.`;
      case 'INVALID_QR':
        return 'The QR code you scanned is not valid or has expired. Please ask staff for assistance.';
      case 'NETWORK_ERROR':
        return 'Unable to load the menu. Please check your internet connection and try again.';
      case 'NO_MENU':
        return 'No menu is currently available for this location. Please contact staff for assistance.';
      case 'CART_CONFLICT':
        return `You have items from "${error.details?.currentVendorName}" in your cart. 
                Would you like to clear your cart and start ordering from "${error.details?.newVendorName}"?`;
      default:
        return error.message;
    }
  };

  const getActions = () => {
    switch (error.type) {
      case 'CART_CONFLICT':
        return (
          <>
            <Button 
              onClick={onContinueWithCart}
              style={{ color: theme.gray[600] }}
            >
              Keep Current Cart
            </Button>
            <Button 
              onClick={() => {
                clearCart();
                onClearCart?.();
              }}
              variant="contained"
              style={{ 
                backgroundColor: theme.primary,
                color: theme.button.primary.text 
              }}
            >
              Clear Cart & Continue
            </Button>
          </>
        );
      case 'VENDOR_MISMATCH':
      case 'INVALID_QR':
        return (
          <>
            <Button 
              onClick={() => navigate('/qr')}
              style={{ color: theme.gray[600] }}
            >
              Scan Another QR
            </Button>
            <Button 
              onClick={onClose}
              variant="contained"
              style={{ 
                backgroundColor: theme.primary,
                color: theme.button.primary.text 
              }}
            >
              Close
            </Button>
          </>
        );
      default:
        return (
          <Button 
            onClick={onClose}
            variant="contained"
            style={{ 
              backgroundColor: theme.primary,
              color: theme.button.primary.text 
            }}
          >
            Try Again
          </Button>
        );
    }
  };

  return (
    <Dialog
      open={!!error}
      onClose={error.type !== 'CART_CONFLICT' ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: theme.components.modal.background,
          color: theme.text,
          borderRadius: '12px'
        }
      }}
    >
      <DialogTitle style={{ 
        borderBottom: `1px solid ${theme.components.card.border}`,
        fontSize: '1.25rem',
        fontWeight: 600
      }}>
        {getErrorTitle()}
      </DialogTitle>
      <DialogContent style={{ paddingTop: '20px' }}>
        <Alert 
          severity={error.type === 'CART_CONFLICT' ? 'warning' : 'error'}
          style={{ 
            backgroundColor: error.type === 'CART_CONFLICT' 
              ? `${theme.warning}20` 
              : `${theme.danger}20`,
            color: theme.text
          }}
        >
          {getErrorMessage()}
        </Alert>
        {error.type === 'VENDOR_MISMATCH' && error.details?.qrCode && (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            backgroundColor: theme.gray[50],
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '0.875rem'
          }}>
            QR Code: {error.details.qrCode}
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ 
        padding: '16px',
        borderTop: `1px solid ${theme.components.card.border}`
      }}>
        {getActions()}
      </DialogActions>
    </Dialog>
  );
};

// Helper function to determine error type from API response
export const getQRErrorType = (error: any): QRCodeErrorHandlerProps['error'] => {
  if (!error) return null;

  // Network errors
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
    return {
      type: 'NETWORK_ERROR',
      message: 'Network connection failed'
    };
  }

  // API error responses
  const status = error.response?.status;
  const data = error.response?.data;

  if (status === 404) {
    return {
      type: 'INVALID_QR',
      message: data?.message || 'QR code not found'
    };
  }

  if (status === 400 && data?.error?.includes('vendor')) {
    return {
      type: 'VENDOR_MISMATCH',
      message: data.error,
      details: data.details
    };
  }

  if (data?.error?.includes('menu')) {
    return {
      type: 'NO_MENU',
      message: data.error
    };
  }

  // Default error
  return {
    type: 'INVALID_QR',
    message: data?.error || error.message || 'Unknown error occurred'
  };
};